import { computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

import type { IChatWithDetails } from '@rpgc-ai/shared-interfaces/interfaces/chat.interface';
import type { IPaginationMeta } from '@rpgc-ai/shared-interfaces/interfaces/chats.interface';

import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { isEmpty, pickBy } from 'lodash-es';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { initialChatsState } from './chats.model';

// Определяем интерфейс для фильтров чатов
export interface IChatsFilters {
  // Поиск по названию чата
  search?: string | null;
  // Страница
  page?: number | null;
  // Ограничение
  limit?: number | null;
}

export const ChatsStore = signalStore(
  { providedIn: 'root' },

  // Redux DevTools поддержка
  withDevtools('chats'),

  withState(initialChatsState),
  withComputed((store) => {
    return {
      sanitizedFilters: computed<IChatsFilters>(() => {
        const filters = store['filters']();
        return pickBy(filters, (value) => value !== null) as IChatsFilters;
      }),
    };
  }),
  withMethods((store) => {
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    /**
     * Инициализация отслеживания query параметров
     * Вызывается автоматически при создании хранилища
     */
    const initRouteParamsSync = (): void => {
      // Слушаем изменения URL параметров и обновляем хранилище
      route.queryParams
        .pipe(
          map((params) => JSON.parse(params['chat_filters'] || '{}')),
          // убираем дубли запросов
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          }),
          map((params) => {
            const sanitizedParams: Partial<IChatsFilters> = {};

            // Обрабатываем строковые параметры
            if ('search' in params) {
              sanitizedParams.search = params['search'] || null;
            }
            if ('page' in params) {
              sanitizedParams.page = params['page'] || null;
            }
            return sanitizedParams;
          }),
          tap((sanitizedParams) => {
            // Используем patchState напрямую вместо метода store.setFilters
            updateState(store, '[Chats] Filters: Sync from Route', {
              filters: {
                ...store['filters'](),
                ...{ search: null },
                ...sanitizedParams,
              },
            });
          }),
        )
        .subscribe();
    };

    // Вызываем инициализацию сразу при создании хранилища
    initRouteParamsSync();

    return {
      /**
       * Устанавливает чаты
       * @param chats
       */
      setChats(chats: IChatWithDetails[]) {
        updateState(store, '[Chats] Chats: Set', { chats });
      },
      setChatsMeta(meta: IPaginationMeta) {
        updateState(store, '[Chats] Chats Meta: Set', { meta });
      },
      resetChatsMeta() {
        updateState(store, '[Chats] Chats Meta: Reset', { meta: null });
      },
      setChatsCount(chatsCount: number) {
        updateState(store, '[Chats] Count: Set', { chatsCount });
      },
      /**
       * Устанавливает загрузку
       * @param isLoading
       */
      setLoading(isLoading: boolean) {
        updateState(store, '[Chats] Loading: Set', { isLoading });
      },

      /**
       * Устанавливает ошибку
       * @param error
       */
      setError(error: string | null) {
        updateState(store, '[Chats] Error: Set', { error });
      },

      /**
       * Очищает хранилище
       */
      clear() {
        updateState(store, '[Chats] State: Clear', initialChatsState);
      },

      /**
       * Устанавливает фильтры (без обновления URL)
       * @param filters
       */
      setFilters(filters: Partial<IChatsFilters>) {
        updateState(store, '[Chats] Filters: Set', { filters: { ...store['filters'](), ...filters } });
      },

      /**
       * Очищает фильтры и обновляет URL
       * @param fields поля для очистки
       */
      clearFilters(fields: { [key: string]: unknown } = {}) {
        const filters = store['filters']();
        const newFilters = isEmpty(fields) ? initialChatsState.filters : { ...filters, ...fields };
        const sanitizedFilters = pickBy(newFilters, (value) => {
          return !isEmpty(value);
        }) as Partial<IChatsFilters>;

        // Очищаем параметры URL
        const currentRoute = router.routerState.snapshot.url.split('?')[0];

        router.navigate([currentRoute], {
          queryParams: {
            chat_filters: JSON.stringify(sanitizedFilters),
          },
          queryParamsHandling: 'merge',
        });
      },

      /**
       * Устанавливает фильтры в роутер
       * @param filters
       */
      setFiltersRouter(filters: Partial<IChatsFilters>) {
        const currentFilters = store['filters']();
        const newFilters = { ...currentFilters, ...filters };
        const sanitizedFilters = pickBy(newFilters, (value) => value !== null && value !== undefined) as Partial<IChatsFilters>;
        const currentRoute = router.routerState.snapshot.url.split('?')[0];

        router.navigate([currentRoute], {
          queryParams: {
            chat_filters: JSON.stringify(sanitizedFilters),
          },
          queryParamsHandling: 'merge',
        });
      },

      /**
       * Устанавливает страницу
       * @param page
       */
      setPage(page: number) {
        updateState(store, '[Chats] Filters Page: Set', { filters: { ...store['filters'](), page } });
      },

      setLimit(limit: number) {
        updateState(store, '[Chats] Filters Limit: Set', { filters: { ...store['filters'](), limit } });
      },
      disableLimit() {
        updateState(store, '[Chats] Filters Limit: Set', { filters: { ...store['filters'](), limit: null } });
      },
      /**
       * Удаляет чат
       * @param chatId - айди удаляемого чата
       **/
      deleteChat(chatId: string) {
        const chats = store.chats();
        if (!chats) {
          return;
        }

        const filteredChats = chats.map((chat) => ({
          ...chat,
          personas: chat.personas.filter((persona) => persona.chatId !== chatId),
        }));

        updateState(store, '[Chats] Chat: Delete', { chats: filteredChats });
      },
      setNavigateOnSuccess(navigateOnSuccess: boolean) {
        updateState(store, '[Chats] Navigate On Success: Set', { navigateOnSuccess });
      },
    };
  }),
);
